import React, { useState } from 'react'
import { createPortal } from 'react-dom';
import { PiLockersFill } from 'react-icons/pi';
import SelectSearch from '../../../../components/ui/SelectSearch';
import { FaCircleCheck } from 'react-icons/fa6';
import { usePlatforms } from '../../../../hooks/usePlatforms';
import { useHistory } from 'react-router-dom';
import { translate } from '../../../../utils/Common';
import TempAlert from '../../../../components/ui/TempAlert';
import Button from '../../../../components/common/Button';
import "../index.css";

const DuplicatePlatformModal = ({platformArray=[], setIsView}) => {
     const [ openExistingPlatform, setOpenExistingPlatform ] = useState(false);
      const [valuePlatformReference, setValuePlatformReference] = useState(null);
    const [valuePlatformName, setValuePlatformName] = useState(null);
    const [valuePlatformExternalId, setValuePlatformExternalId] = useState(null);
    const [statusByJob, setStatusByJob] = useState({
      general: null,
      settings: null,
      modules: null,
      doors: null
    });
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);

    const { duplicatePlatform } = usePlatforms();
    const history = useHistory();

    const handleClose = () => {
        setIsView(false);
        setOpenExistingPlatform(false);
        setValuePlatformReference(null);
        setValuePlatformName(null);
        setValuePlatformExternalId(null);
        setStatusByJob({ general: null, settings: null, modules: null, doors: null });
    }

    const handleCloseErrorAlert = () => {
        setShowErrorAlert(false);
        setErrorDetails(null);
    }

    const handleCloseSuccessAlert = () => {
        setShowSuccessAlert(false);
    }

    const handleCreateDuplicatePlatform = async () => {
      if (valuePlatformReference && valuePlatformName && valuePlatformExternalId) {
        const data = {
          referencePlatformId: valuePlatformReference,
          namePlatform: valuePlatformName,
          externalId: valuePlatformExternalId
        };
  
        const jobs = ['general', 'settings', 'modules', 'doors'];
        const controller = new AbortController();
        let error = false;
        let currentErrorDetails = null;
  
        for (const job of jobs) {
          setStatusByJob(prev => ({ ...prev, [job]: 'loading' }));
          try {
            await duplicatePlatform(job, data, controller.signal);
            setStatusByJob(prev => ({ ...prev, [job]: 'success' }));
          } catch (e) {
            setStatusByJob(prev => ({ ...prev, [job]: 'error' }));
            error = true;
            
            // Parse error details
            try {
              const errorData = JSON.parse(e.message);
              console.log("errorData", errorData);
              
              currentErrorDetails = {
                job: translate(`dashboard.platforms.duplicateModal.${job}Job`, "upper"),
                status: errorData.message,
                code: errorData.code,
                message: errorData.message
              };
            } catch (parseError) {
              currentErrorDetails = {
                job: translate(`dashboard.platforms.duplicateModal.${job}Job`),
                status: 'UNKNOWN',
                code: 'PARSE_ERROR',
                message: e.message || 'Error desconocido'
              };
            }
            
            console.error(`Error duplicando plataforma en job ${job}:`, e);
            
            // Show error alert with details
            setErrorDetails(currentErrorDetails);
            setShowErrorAlert(true);
            break; // Stop processing other jobs if one fails
          }
        }
  
        if (!error) {
          // Show success alert
          setShowSuccessAlert(true);
          
          // Navigate after 3 seconds
          setTimeout(() => {
            history.push(`/dashboard/platforms/settings?extId=${valuePlatformExternalId}&view=edit`);
          }, 3000);
        }
      }
    };

  // Portal a body: el Sidebar es fixed z-[1000] y tapaba la mitad izquierda del modal
  return createPortal(
    <div className="modal-container" role="dialog" aria-modal="true">
      <div className="modal-box">
                                  <h3 className="modal-title">
                            {openExistingPlatform && <PiLockersFill size={25} className='mr-2' />}
                            {openExistingPlatform ? translate("dashboard.platforms.duplicateModal.createFromExisting") : translate("global.chooseOption")}
                          </h3>

        {!openExistingPlatform ? (
          <div className="modal-options">
            {/* Opción: Crear desde cero */}
            <div className="option-card" onClick={() => (window.location = "/dashboard/platforms/settings?view=create")}>
              <img src="/img/icon_platform_new.png" alt={translate("dashboard.platforms.duplicateModal.iconNew")} className="option-img" />
              <h3 className="option-title">{translate("dashboard.platforms.duplicateModal.createNewPlatform")}</h3>
              <p className="option-description">{translate("dashboard.platforms.duplicateModal.descriptionNewPlatform")}</p>
            </div>

            {/* Opción: Usar existente */}
            <div className="option-card" onClick={() => setOpenExistingPlatform(true)}>
              <img src="/img/icon_platform_duplicate.png" alt={translate("dashboard.platforms.duplicateModal.iconDuplicate")} className="option-img" />
              <h3 className="option-title">{translate("dashboard.platforms.duplicateModal.duplicateBasedOnExisting")}</h3>
              <p className="option-description">{translate("dashboard.platforms.duplicateModal.descriptionDuplicatePlatform")}</p>
            </div>
          </div>
        ) : (
          <div className="modal-options">
            <div className="option-card non-clickable" style={{ textAlign: "left" }}>
              <h3 className="option-title mb-2">{translate("global.locker")} {translate("global.reference")}</h3>

              <SelectSearch
                options={platformArray.map(platform => ({
                  value: platform?.locationId,
                  label: platform?.locationName
                }))}
                nameInput="platformReference"
                isDisabled={false}
                isLoading={false}
                value={valuePlatformReference}
                setValue={(name, value) => setValuePlatformReference(value)}
              />

              <h5 className="option-title mt-4">{translate("global.locker")} {translate("global.toCreate")}</h5>
              <div className="flex items-center" style={{ gap: "10px" }}>
                <input
                  type="text"
                  placeholder={`${translate("global.locker")} ${translate("global.name")}`}
                  className="h-11 rounded-lg border border-transparent bg-muted/60 text-sm w-full px-3 text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed"
                  disabled={!valuePlatformReference}
                  onChange={(e) => setValuePlatformName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder={translate("global.ExternalID")}
                  className="h-11 rounded-lg border border-transparent bg-muted/60 text-sm w-full px-3 text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed"
                  disabled={!valuePlatformReference}
                  onChange={(e) => setValuePlatformExternalId(e.target.value)}
                />
                <Button
                  variant="primary"
                  leftIcon={<FaCircleCheck />}
                  loading={Object.values(statusByJob).some(status => status === 'loading')}
                  disabled={
                    !valuePlatformReference || !valuePlatformName || !valuePlatformExternalId ||
                    Object.values(statusByJob).some(status => status === 'loading')
                  }
                  onClick={handleCreateDuplicatePlatform}
                >
                  {translate("global.create")}
                </Button>
              </div>

              {/* Estado visual de cada job - Diseño mejorado */}
              <div className="status-list">
                {[
                  { key: 'general', label: translate("dashboard.platforms.duplicateModal.creatingGeneralData"), number: 1 },
                  { key: 'settings', label: translate("dashboard.platforms.duplicateModal.configuringSettings"), number: 2 },
                  { key: 'modules', label: translate("dashboard.platforms.duplicateModal.duplicatingModules"), number: 3 },
                  { key: 'doors', label: translate("dashboard.platforms.duplicateModal.addingDoors"), number: 4 }
                ].map(({ key, label, number }) => {
                  const status = statusByJob[key];
                  
                  const getStatusInfo = (status) => {
                    switch (status) {
                      case 'loading':
                        return {
                          icon: <div className="status-icon loading"><div className="spinner"></div></div>,
                          text: translate("global.inProcess"),
                          className: 'status-item loading'
                        };
                      case 'success':
                        return {
                          icon: <div className="status-icon success">✓</div>,
                          text: translate("global.completed"),
                          className: 'status-item success'
                        };
                      case 'error':
                        return {
                          icon: <div className="status-icon error">✗</div>,
                          text: translate("global.error"),
                          className: 'status-item error'
                        };
                      default:
                        return {
                          icon: <div className="status-icon pending">{number}</div>,
                          text: translate("global.pending"),
                          className: 'status-item pending'
                        };
                    }
                  };

                  const statusInfo = getStatusInfo(status);

                  return (
                    <div key={key} className={statusInfo.className}>
                      <div className="status-content">
                        {statusInfo.icon}
                        <span className="status-label">{label}</span>
                      </div>
                      <span className="status-text">{statusInfo.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <button className="close-button" onClick={handleClose} disabled={Object.values(statusByJob).some(status => status === 'loading')}>×</button>
      </div>
      
      {/* Professional Error Alert */}
      <TempAlert
        type="error"
        title={translate("dashboard.platforms.duplicateModal.errorTitle")}
        message={translate("dashboard.platforms.duplicateModal.errorMessage")}
        details={errorDetails}
        isVisible={showErrorAlert}
        onClose={handleCloseErrorAlert}
        autoClose={true}
        autoCloseDelay={9000}
      />
      
      {/* Professional Success Alert */}
      <TempAlert
        type="success"
        title={translate("dashboard.platforms.duplicateModal.successTitle")}
        message={translate("dashboard.platforms.duplicateModal.successMessage")}
        isVisible={showSuccessAlert}
        onClose={handleCloseSuccessAlert}
        autoClose={true}
        autoCloseDelay={9000}
      />
      
      {/* <ToastContainer position="top-right" autoClose={3000} hideProgressBar /> */}
    </div>,
    document.body
  );
}

export default DuplicatePlatformModal